// Makes a 2 dimensional array 1 dimensional
// Can replace this with Array.prototype.flat
export const flatten = <T>(arr: T[][]): T[] => {
  return ([] as T[]).concat(...arr);
};

// Group array of items according to a criteria (string or function). The return value of the criteria will be the key name in the result.
export const groupBy = <T>(
  arr: Array<T>,
  criteria: string | ((arg: T) => any),
): { [key: string]: Array<T> } => {
  return arr.reduce((obj, item) => {
    // Check if the criteria is a function to run on the item or a property of it
    const key =
      // @ts-ignore
      typeof criteria === 'function' ? criteria(item) : item[criteria];

    // If the key doesn't exist yet, create it
    if (!obj.hasOwnProperty(key)) {
      // @ts-ignore
      obj[key] = [];
    }

    // Push the value to the object
    // @ts-ignore
    obj[key].push(item);

    // Return the object to the next item in the loop
    return obj;
  }, {});
};

// Merge and deduplicate array of items by a key.
export const mergeAndDedupeByKey = <T>(
  allItems: T[],
  nextItems: T[],
  key: string,
): T[] => {
  const merged = [...(allItems || []), ...(nextItems || [])];
  const ids: Record<string, boolean> = {};
  const deduped: T[] = [];

  const noItems = !merged[0];
  const keyInvalid =
    !key ||
    // @ts-ignore
    (merged[0] && !merged[0][key]);
  if (noItems || keyInvalid) {
    return merged;
  }

  merged.forEach((item) => {
    // @ts-ignore
    if (!ids[item[key]]) {
      // @ts-ignore
      ids[item[key]] = true;
      deduped.push(item);
    }
  });

  return deduped;
};

// Merge and deduplicate any type by identifer function.
// Deduplication strategy is to use the last item encountered when traversing the array.
// Main difference compared to mergeAndDedupeByKey is that this method can work with any type, not just objects.
export const mergeAndDedupeByIdentifier = <T>(
  allItems: T[],
  nextItems: T[],
  identifierFn: (item1: T) => string,
): T[] => {
  const merged = [...(allItems || []), ...(nextItems || [])];
  const ids: Record<string, boolean> = {};
  const deduped: T[] = [];

  const noItems = !merged[0];

  if (noItems) {
    return merged;
  }

  merged.forEach((item) => {
    const identifier = identifierFn(item);
    if (!ids[identifier]) {
      ids[identifier] = true;
      deduped.push(item);
    }
  });

  return deduped;
};

// Converts an array to an object where the keys of the object are the ${property} of the array, and its values are the array values itself.
export const toObject = <T>(
  array: T[],
  property: keyof T,
): { [key: string]: T } => {
  const newObject: any = {};

  array.forEach((arrayElement) => {
    const key = arrayElement[property];

    // If key does not exist in an array element, then show the element and the property that is missing
    if (key === undefined) {
      throw new Error(
        `${JSON.stringify(arrayElement)} does not have property ${String(
          property,
        )}`,
      );
    }
    newObject[key] = arrayElement;
  });

  return newObject as { [key: string]: T };
};
